/**
 * Calibración de pixel spacing por distancia conocida (mm).
 */

/**
 * @param {object} data measurementData de Length (cornerstone-tools v4)
 * @returns {{ x: number, y: number } | null}
 */
function handlePoint(handle) {
  if (!handle || handle.x == null || handle.y == null) return null
  return { x: Number(handle.x), y: Number(handle.y) }
}

/**
 * Longitud física actual (mm) a partir de handles + spacing.
 * @param {object} data
 * @param {number} rowSp mm/px fila
 * @param {number} colSp mm/px columna
 */
export function physicalLengthFromHandles(data, rowSp, colSp) {
  if (!data || !data.handles) return 0
  const start = handlePoint(data.handles.start || data.handles[0])
  const end = handlePoint(data.handles.end || data.handles[1])
  if (!start || !end) {
    const len = Number(data.length)
    return Number.isFinite(len) && len > 0 ? len : 0
  }
  const row = rowSp > 0 ? rowSp : 1
  const col = colSp > 0 ? colSp : 1
  const dx = (end.x - start.x) * col
  const dy = (end.y - start.y) * row
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Factor uniforme para escalar row/col spacing.
 * @returns {number|null}
 */
export function computeUniformScaleFromLength(data, knownMm, rowSp, colSp) {
  const known = Number(knownMm)
  if (!(known > 0)) return null
  const current = physicalLengthFromHandles(data, rowSp, colSp)
  if (!(current > 0)) return null
  return known / current
}

/**
 * Recalcula valores mostrados de una medición tras cambiar spacing.
 * @param {string} tool
 * @param {object} data
 * @param {number} rowSp
 * @param {number} colSp
 */
export function recalculateMeasurementData(tool, data, rowSp, colSp) {
  if (!data) return data
  const row = rowSp > 0 ? rowSp : 1
  const col = colSp > 0 ? colSp : 1

  if (tool === 'Length') {
    const len = physicalLengthFromHandles(data, row, col)
    data.length = len
    data.unit = 'mm'
    return data
  }

  if (tool === 'EllipticalRoi' || tool === 'RectangleRoi') {
    const stats = data.cachedStats
    if (stats && stats.area != null && data.handles) {
      const scale = row * col
      if (stats.areaPixel != null) {
        stats.area = stats.areaPixel * scale
      } else if (scale > 0) {
        stats.area = stats.area / scale * scale
      }
    }
    return data
  }

  if (tool === 'FreehandRoi' && data.area != null && data.areaPixel != null) {
    data.area = data.areaPixel * row * col
    return data
  }

  return data
}

/**
 * Aplica spacing calibrado a un objeto image de Cornerstone.
 * @param {object} image
 * @param {{ rowPixelSpacing: number, columnPixelSpacing: number }} spacing
 */
export function applySpacingToImage(image, spacing) {
  if (!image || !spacing) return
  if (spacing.rowPixelSpacing > 0) {
    image.rowPixelSpacing = spacing.rowPixelSpacing
  }
  if (spacing.columnPixelSpacing > 0) {
    image.columnPixelSpacing = spacing.columnPixelSpacing
  }
}

/**
 * Lee mediciones del toolState de Cornerstone en el elemento.
 * @param {HTMLElement} element
 * @param {string[]} toolNames
 * @returns {Array<{ tool: string, note: string, data: object }>}
 */
export function collectMeasurementsFromElement(element, toolNames, cornerstoneTools) {
  if (!element || !cornerstoneTools || typeof cornerstoneTools.getToolState !== 'function') {
    return []
  }
  const out = []
  ;(toolNames || []).forEach((tool) => {
    try {
      const state = cornerstoneTools.getToolState(element, tool)
      if (!state || !Array.isArray(state.data)) return
      state.data.forEach((data) => {
        out.push({ tool, note: '', data })
      })
    } catch (e) {
      /* ignore */
    }
  })
  return out
}

/**
 * Conserva notas previas emparejando por uuid de medición.
 * @param {Array<{ note?: string, data?: object }>} prev
 * @param {Array<{ note?: string, data?: object }>} next
 */
export function preserveMeasurementNotes(prev, next) {
  const noteByUuid = new Map()
  ;(prev || []).forEach((m) => {
    const uid = m && m.data && m.data.uuid
    if (uid) noteByUuid.set(uid, m.note || '')
  })
  return (next || []).map((m) => {
    const uid = m.data && m.data.uuid
    const note = uid && noteByUuid.has(uid) ? noteByUuid.get(uid) : m.note || ''
    return { ...m, note }
  })
}
